import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Check, CrosshairSimple, MapPinLine } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getGeoPosition, geoErrorMessage } from "../lib/geolocation";

const SORRISO = { lat: -12.545, lng: -55.711 };
const MIN_KM = 1;
const MAX_KM = 150;
const DEFAULT_KM = 30;

const CENTER_HTML =
  '<div style="width:26px;height:26px;transform:translateY(-4px)">' +
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="#004E98" stroke="#fff" stroke-width="1.5">' +
  '<path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/>' +
  '<circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></div>';

// Seletor de RAIO de busca (estilo iFood): mapa com pino central arrastável + círculo
// ao vivo controlado por um slider (1–150 km, padrão 30). Retorna { lat, lng, radius_km }.
// props: { initial:{lat,lng,radius_km}|null, onConfirm({lat,lng,radius_km}), onClose() }
export default function RadiusPicker({ initial, onConfirm, onClose }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [pos, setPos] = useState(
    initial && initial.lat ? { lat: initial.lat, lng: initial.lng } : SORRISO
  );
  const [km, setKm] = useState(
    initial && initial.radius_km ? Math.min(MAX_KM, Math.max(MIN_KM, initial.radius_km)) : DEFAULT_KM
  );
  const [locating, setLocating] = useState(false);

  const fitCircle = () => {
    if (!mapRef.current || !circleRef.current) return;
    try {
      mapRef.current.fitBounds(circleRef.current.getBounds(), { padding: [40, 40] });
    } catch { /* noop */ }
  };

  // Inicializa o mapa uma vez
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([pos.lat, pos.lng], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    const icon = L.divIcon({ className: "aj-radius-pin", html: CENTER_HTML, iconSize: [26, 26], iconAnchor: [13, 26] });
    const marker = L.marker([pos.lat, pos.lng], { draggable: true, icon }).addTo(map);
    const circle = L.circle([pos.lat, pos.lng], {
      radius: km * 1000,
      color: "#004E98",
      weight: 2,
      fillColor: "#004E98",
      fillOpacity: 0.12,
    }).addTo(map);
    const onMove = (lat, lng) => {
      circle.setLatLng([lat, lng]);
      setPos({ lat, lng });
    };
    marker.on("drag", () => { const ll = marker.getLatLng(); circle.setLatLng(ll); });
    marker.on("dragend", () => { const ll = marker.getLatLng(); onMove(ll.lat, ll.lng); });
    map.on("click", (e) => { marker.setLatLng(e.latlng); onMove(e.latlng.lat, e.latlng.lng); });
    mapRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;
    const sizeTimer = setTimeout(() => { if (mapRef.current) { map.invalidateSize(); fitCircle(); } }, 250);
    return () => { clearTimeout(sizeTimer); map.remove(); mapRef.current = null; markerRef.current = null; circleRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualiza o raio do círculo quando o slider muda
  useEffect(() => {
    if (!circleRef.current) return;
    circleRef.current.setRadius(km * 1000);
    fitCircle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [km]);

  const useMyLocation = () => {
    setLocating(true);
    getGeoPosition({ timeout: 12000 })
      .then((p) => {
        const lat = p.coords.latitude, lng = p.coords.longitude;
        if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
        if (circleRef.current) circleRef.current.setLatLng([lat, lng]);
        setPos({ lat, lng });
        fitCircle();
      })
      .catch((e) => toast.error(geoErrorMessage(e)))
      .finally(() => setLocating(false));
  };

  return (
    <div className="fixed inset-0 z-[1010] bg-black/60 flex flex-col" data-testid="radius-picker">
      <div className="bg-white flex items-center justify-between p-3 gap-2 shadow pt-safe">
        <h3 className="font-heading font-bold text-base flex items-center gap-1.5">
          <MapPinLine size={18} weight="bold" className="text-[#004E98]" /> Definir raio de busca
        </h3>
        <button type="button" onClick={onClose} className="p-1 text-slate-500" data-testid="radius-close">
          <X size={22} weight="bold" />
        </button>
      </div>

      <div className="relative flex-1">
        <div ref={mapEl} className="absolute inset-0" data-testid="radius-canvas" />
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[15] bg-black/70 text-white text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none">
          Arraste o pino e ajuste o raio abaixo
        </div>
      </div>

      <div className="bg-white p-4 space-y-3 shadow-inner pb-safe">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-full border-2 border-[#004E98] text-[#004E98] font-bold text-sm hover:bg-[#004E98]/5"
          data-testid="radius-use-location"
        >
          <CrosshairSimple size={16} weight="bold" />
          {locating ? "Localizando..." : "Centralizar na minha localização"}
        </button>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Raio</span>
            <span className="text-lg font-black text-[#004E98]" data-testid="radius-value">até {km} km</span>
          </div>
          <input
            type="range"
            min={MIN_KM}
            max={MAX_KM}
            step={1}
            value={km}
            onChange={(e) => setKm(parseInt(e.target.value, 10))}
            className="w-full accent-[#004E98]"
            data-testid="radius-slider"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
            <span>{MIN_KM} km</span>
            <span>{MAX_KM} km</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => { onConfirm({ lat: pos.lat, lng: pos.lng, radius_km: km }); onClose(); }}
          className="btn-primary w-full"
          data-testid="radius-confirm-btn"
        >
          <Check size={18} weight="bold" /> Aplicar raio de {km} km
        </button>
      </div>
    </div>
  );
}
